import type {
  ChatTraceContext,
  ChatWSClientToolResult,
  ChatWSPostInferenceMessageBlock,
  InboundResult,
  WSOutboundFrame,
} from './types';
import type { StreamingEvent } from '@circuit/chat-utils';

import {
  assertSecureWebSocketURL,
  classifyClose,
  jitteredBackoffDelay,
  RECONNECT_FAILURE_ERROR_THRESHOLD,
  RECONNECT_MAX_MS,
  RECONNECT_MIN_MS,
} from '@circuit/api/websocket';

import {
  clearClient as registryClearClient,
  setClient as registrySetClient,
} from './chatWSClientRegistry';

/**
 * Per-chat WebSocket client for the Circuit chat WS transport.
 *
 * One instance owns one socket bound to a single `chatID`. The instance
 * runs the auth handshake on (re-)connect, dispatches inbound frames as
 * `StreamingEvent`s to the active turn handler, and exposes
 * `submit` / `resumeToolUse` / `cancel` outbound frames.
 *
 *
 * Invariants:
 *  - one active connection attempt at a time (shared `connectPromise`)
 *  - one active socket at a time (older sockets are invalidated by
 *    bumping `connectionId`; stale listeners no-op on identity check)
 *  - one active turn handler per chat (`setTurnHandler` replaces; the
 *    connection store + `useStreamChat` serialize turns at the call site)
 *  - auth resolve/reject is owned by `openSocket()` — no spooky action
 *    from `handleAuthOK` / `handleAuthError`
 *  - socket close cleanup lives in the `close` event handler; everything
 *    else just calls `socket.close()` and lets that handler run
 */

/** Token getter — injected so the client doesn't depend on AuthProvider. */
export type ChatWSTokenGetter = () => Promise<string> | string;

/**
 * Trace fields (`trace_id` / `parent_id`) for the BE `WSInboundMessage`, or an
 * empty object when absent so non-traced frames stay byte-identical.
 */
function traceContextFrameFields(
  traceContext?: ChatTraceContext,
): Record<string, string> {
  if (!traceContext) return {};
  return { parent_id: traceContext.parentId, trace_id: traceContext.traceId };
}

/**
 * Chat WS lifecycle status (snake_case).
 *
 * The notifications `WebSocketClient` in `@circuit/api/websocket` uses a
 * PascalCase `ConnectionStatus` enum. To bridge between the two casings
 * without a full unification, use `mapChatStatusToConnectionStatus` from
 * `@circuit/api/websocket`. Keep this union in sync with the
 * `ChatWSStatusLike` duplicate declared there.
 */
export type ChatWSStatus =
  | 'auth_failed'
  | 'closed'
  | 'connecting'
  | 'idle'
  | 'open';

export type ChatWSStatusListener = (
  status: ChatWSStatus,
  isAuthenticated: boolean,
) => void;

/**
 * Per-turn handler. The chat WS server multiplexes events for the
 * currently in-flight turn back over the socket. The active handler
 * receives every inbound `StreamingEvent` until cleared.
 */
export interface ChatWSTurnHandler {
  /** Flag flipped to true when the consumer aborts; events emitted after this point are dropped. */
  ignoreAfterAbort: { value: boolean };
  /**
   * The in-flight turn's per-turn requestID (uuidv7), set by chatWSService when
   * it attaches the handler. The socket layer doesn't own turn identity — it
   * just reads this so socket-close/open telemetry can correlate to a turn when
   * one is running. Optional: socket-lifecycle events fire with no turn too.
   */
  requestID?: string;
  onError: (error: Error) => void;
  onEvent: (event: StreamingEvent) => void;
  /**
   * Fired when a reconnect completes: the socket re-authed and the `rejoin`
   * frame was sent, so the server is replaying/tailing the turn again. The
   * counterpart to `onReconnecting` — lets the consumer clear the
   * 'reconnecting' state as soon as the *connection* is healthy rather than
   * waiting for the next event (which a turn inside a long silent tool call
   * may not produce for minutes). Optional.
   */
  onReconnected?: () => void;
  /**
   * Fired when an established session's socket drops mid-turn and the client
   * is transparently reconnecting (`shouldReconnect`). The turn is NOT failed
   * — on re-auth the client sends `rejoin` and the stream resumes. Lets the
   * consumer surface a recoverable 'reconnecting' state instead of waiting in
   * silence. May fire again for each subsequent failed reconnect attempt.
   * Optional: consumers that don't care can omit it.
   */
  onReconnecting?: () => void;
}

export interface ChatWSLogger {
  error: (entry: {
    data?: Record<string, unknown>;
    error?: unknown;
    message: string;
  }) => void;
  warn: (entry: { data?: Record<string, unknown>; message: string }) => void;
}

/**
 * Telemetry seam for socket-lifecycle events. Injected the same way as
 * `getAccessToken` / `onAuthFailed` / `webSocketImpl` so the client stays free
 * of the app's logger/RUM wiring (and defaults to no-op in tests). Distinct
 * from the diagnostic `ChatWSLogger`: the connection manager supplies an
 * implementation that routes through `emitChatTelemetry`.
 */
export interface ChatWSTelemetry {
  socketClosed: (info: {
    /** From classifyClose() -- additive field, existing dashboards unaffected. */
    closeClassification?: { level: string; reason: string };
    closeCode?: number;
    reason?: string;
    requestID?: string;
    wasClean?: boolean;
  }) => void;
  socketOpened: (info: {
    reconnectAttempt: number;
    requestID?: string;
  }) => void;
}

/**
 * Timing seam for socket-lifecycle Duration Vitals (RUM `@type:vital`).
 * Mirrors `ChatWSTelemetry`'s injection but drives a different RUM primitive:
 * `ChatWSTelemetry` emits discrete events, this records duration measurements.
 * No-op default for tests; the connection manager wires the real
 * `createChatWSTracing` implementation.
 */
export interface ChatWSTracing {
  auth: (info: {
    durationMs: number;
    requestID?: string;
    startTimeMs: number;
  }) => void;
  socketOpen: (info: {
    durationMs: number;
    requestID?: string;
    startTimeMs: number;
  }) => void;
}

export interface ChatWSClientConfig {
  /** Auth handshake timeout. Must match `chatWSAuthTimeout` server-side (10s). */
  authTimeoutMs?: number;
  /**
   * Optional. If omitted, chatID is resolved from the server's `auth_ok`
   * payload (`data.chat_id`) — the API-key auth path where the BE mints or
   * looks up the chat by (agentKey, sessionToken). Passed at construction
   * for the JWT path where chatID is known from the URL. When resolved
   * post-auth, the client self-registers via `setClient(chatID, this)`.
   */
  chatID?: string;
  /** Reject `ws://` URLs before connecting. Callers inject prod-detection. */
  forbidInsecureScheme?: boolean;
  getAccessToken: ChatWSTokenGetter;
  /** Test seam — see HANDSHAKE_TURN_RETRY_DEADLINE_MS. */
  handshakeTurnRetryDeadlineMs?: number;
  logger?: ChatWSLogger;
  maxConsecutiveAuthErrors?: number;
  /** Socket-lifecycle telemetry seam (no-op default). */
  telemetry?: ChatWSTelemetry;
  /** Socket-lifecycle timing (Duration Vitals) seam (no-op default). */
  tracing?: ChatWSTracing;
  url: string;
  /** Test seam — defaults to globalThis.WebSocket. */
  webSocketImpl?: typeof WebSocket;
  /**
   * Optional — sources the embedding page's origin for the auth frame's
   * `host_origin`. The browser stamps the WS Origin with the iframe's own
   * origin, so the API-key BE validates this against the key's allowed
   * domains instead (the WS analogue of `X-Host-Origin`). Omit for JWT.
   */
  getHostOrigin?: () => string | null | undefined;
  /**
   * Optional — called synchronously (or async) on every auth
   * handshake to source the current session_token. When it returns a
   * non-empty string the token is attached to the auth frame as
   * `session_token`, letting the API-key BE resolve the existing chat
   * instead of minting a fresh one. Omit for JWT transport.
   */
  getSessionToken?: () => string | null | undefined;
  /** Called when the server returns auth_error past the retry budget. */
  onAuthFailed?: () => void;
  /**
   * Called when a re-auth's `auth_ok` resolves a different chatID than the one
   * this client is bound to. The client skips `rejoin` and disconnects itself
   * right after the callback returns, so no frame can reach the new chat;
   * consumers should drop their reference and settle any in-flight turn.
   */
  onChatIDChanged?: (previousChatID: string, nextChatID: string) => void;
  /**
   * Called when auth_ok resolves a chatID post-hoc (API-key path). Fires
   * *after* `bindChatID` has run so the registry lookup is guaranteed to
   * return `this`. Optional; the JWT path can omit it.
   */
  onChatIDResolved?: (chatID: string) => void;
  /**
   * Called with the session_token from the server's `auth_ok` payload
   * (`data.session_token`). API-key transport uses this to persist the
   * BE-minted (or echoed) token for subsequent reconnects. Optional; the
   * JWT path can omit it. Fires at most once per successful auth.
   */
  onSessionToken?: (sessionToken: string) => void;
  /** Test seam for reconnect jitter — defaults to Math.random. */
  random?: () => number;
  /**
   * Optional client-side policy: given the token last sent in the auth frame,
   * return true to re-authenticate before a user-turn send (e.g. expired, or
   * inside a refresh margin). Not an authoritative check — when omitted, the
   * client relies on the server's `auth_expired` rejection and recovery.
   * Omit for API-key transport.
   */
  shouldReauthenticate?: (token: string) => boolean;
}

const DEFAULT_AUTH_TIMEOUT_MS = 10_000;
const MAX_AUTH_EXPIRED_RESENDS = 3;
/**
 * Wall-clock budget for pre-auth handshake retries on a user-turn
 * submit/resume. A failed submit never reached the server, so only a fresh
 * connect — not `rejoin` — can recover it.
 *
 * Deadline- not attempt-bounded: a fast 1006 close costs ~0 per attempt, so
 * attempt-counting burned this budget in <2s. Must stay strictly below
 * RECONNECT_WATCHDOG_MS (45s) in useStreamChat; the test there enforces it.
 */
export const HANDSHAKE_TURN_RETRY_DEADLINE_MS = 30_000;
/**
 * Retry budget for a user-turn frame once the reconnect streak has hit its
 * threshold — bounds the immediate resume attempt so a genuine outage still
 * degrades to NETWORK_ERROR quickly.
 */
export const SUSTAINED_OUTAGE_TURN_RETRY_DEADLINE_MS = 5_000;
/** Structural error name so routing matches handshake timeouts by name, not by message string. */
export const HANDSHAKE_TIMEOUT_ERROR_NAME = 'HandshakeTimeoutError';
/**
 * A genuine auth rejection settles as AuthError before the close frame
 * arrives, so this name is only assigned to a close that wasn't preceded by
 * one — see `onSocketClose`'s 1008 exclusion.
 */
export const HANDSHAKE_CLOSED_ERROR_NAME = 'HandshakeClosedError';

/** Close code the server uses for a policy-violation (auth-reject) close. */
const CLOSE_POLICY_VIOLATION = 1008;
/**
 * Stamped on a re-tagged error when a turn's start frame never left the
 * client (pre-auth handshake failure). Consumers fork on it: a never-started
 * turn has nothing server-side to poll for and its message was never
 * persisted, so the mid-stream 'still processing' promise would be a lie.
 */
export const TURN_NOT_STARTED_MARKER = 'turnNotStarted';

export type TurnNotStartedError = Error & { [TURN_NOT_STARTED_MARKER]: true };

/** `error_type` the BE echoes for a turn frame rejected because the authed JWT expired. */
const AUTH_EXPIRED_ERROR_TYPE = 'auth_expired';

/**
 * Stamps `err` as never-started and retags handshake/abort/blank names to
 * `NetworkError` so `useStreamChat` routes it as a network error.
 */
export function markTurnNotStarted(
  err: unknown,
  opts?: { retagAuthError?: boolean },
): TurnNotStartedError {
  let wrapped: Error = err instanceof Error ? err : new Error(String(err));
  if (opts?.retagAuthError === true && wrapped.name === 'AuthError') {
    const retagged = new Error(wrapped.message);
    (retagged as Error & { cause?: unknown }).cause = wrapped;
    wrapped = retagged;
    wrapped.name = 'NetworkError';
  }
  const stamped = wrapped as Error & { [TURN_NOT_STARTED_MARKER]?: true };
  stamped[TURN_NOT_STARTED_MARKER] = true;
  if (
    wrapped.name === 'AbortError' ||
    isRetryableHandshakeError(wrapped) ||
    !wrapped.name ||
    wrapped.name === 'Error'
  ) {
    wrapped.name = 'NetworkError';
  }
  return wrapped as TurnNotStartedError;
}

/** True when `err` was stamped by `markTurnNotStarted` as a never-started turn. */
export function isTurnNotStartedError(err: unknown): boolean {
  return (
    err instanceof Error &&
    (err as Partial<TurnNotStartedError>)[TURN_NOT_STARTED_MARKER] === true
  );
}
/** Shared by the submit-path retry loop and `markTurnNotStarted`'s error re-tagging. */
export function isRetryableHandshakeError(err: unknown): boolean {
  return (
    err instanceof Error &&
    (err.name === HANDSHAKE_TIMEOUT_ERROR_NAME ||
      err.name === HANDSHAKE_CLOSED_ERROR_NAME)
  );
}

// Jittered so a mass disconnect doesn't retry in lockstep; the floor keeps a
// fast-failing close out of a tight loop and the cap yields ~6-8 attempts
// across the 30s deadline.
const HANDSHAKE_RETRY_BACKOFF_MIN_MS = 500;
const HANDSHAKE_RETRY_BACKOFF_MAX_MS = 4_000;

async function sleepMs(ms: number): Promise<void> {
  await new Promise<void>((resolve) => {
    setTimeout(resolve, ms);
  });
}

/**
 * Context threaded from `openSocket` into its socket event-listener helpers.
 * Avoids long positional param lists while keeping all state in one place.
 */
interface SocketHandlerContext {
  connectStartedAt: number;
  socket: WebSocket;
  clearAuthTimer: () => void;
  isCurrent: () => boolean;
  settleReject: (err: Error) => void;
  settleResolve: () => void;
}

/** Parse and validate a raw inbound WebSocket frame. Returns null (and logs) on any failure. */
function parseRawFrame(
  raw: unknown,
  logger: ChatWSLogger | undefined,
): WSOutboundFrame | null {
  if (typeof raw !== 'string') {
    logger?.warn({
      data: { kind: typeof raw },
      message: 'Chat WS non-string frame',
    });
    return null;
  }
  let frame: WSOutboundFrame;
  try {
    frame = JSON.parse(raw) as WSOutboundFrame;
  } catch (err: unknown) {
    logger?.error({ error: err, message: 'Chat WS frame JSON parse failed' });
    return null;
  }
  if (frame == null || typeof frame.event !== 'string') {
    logger?.warn({
      data: { frame: raw },
      message: 'Chat WS frame missing event',
    });
    return null;
  }
  return frame;
}

/** Arms the auth handshake timeout. Returns a cancel function. */
function scheduleAuthTimeout(
  isCurrent: () => boolean,
  socket: WebSocket,
  settleReject: (err: Error) => void,
  authTimeoutMs: number,
): () => void {
  const authTimer = setTimeout(() => {
    if (!isCurrent()) return;
    const err = new Error('Chat WS auth handshake timed out');
    err.name = HANDSHAKE_TIMEOUT_ERROR_NAME;
    settleReject(err);
    try {
      socket.close();
    } catch {
      /* ignore */
    }
  }, authTimeoutMs);
  return () => clearTimeout(authTimer);
}

/** The last user-turn frame sent, kept so an `auth_expired` rejection can re-send it. */
interface PendingTurnFrame {
  deadline?: number;
  handler: ChatWSTurnHandler;
  payload: Record<string, unknown>;
  requestID: string;
  resends: number;
}

export class ChatWSClient {
  private readonly url: string;
  // Not readonly: API-key auth resolves chatID from the auth_ok payload after
  // construction; JWT auth binds it in the constructor.
  private chatID: string | undefined;
  // Not readonly: the connection manager refreshes these on re-acquire so the
  // long-lived client always uses the freshest token getter / auth handler
  // (see `updateAuth`).
  private getAccessToken: ChatWSTokenGetter;
  private readonly getSessionToken?: () => string | null | undefined;
  private readonly getHostOrigin?: () => string | null | undefined;
  private readonly authTimeoutMs: number;
  private readonly handshakeTurnRetryDeadlineMs: number;
  private readonly shouldReauthenticate?: (token: string) => boolean;
  private readonly maxConsecutiveAuthErrors: number;
  private readonly logger?: ChatWSLogger;
  private readonly WebSocketImpl: typeof WebSocket;
  private onAuthFailed?: () => void;
  private readonly onChatIDResolved?: (chatID: string) => void;
  private readonly onChatIDChanged?: (
    previousChatID: string,
    nextChatID: string,
  ) => void;
  private readonly onSessionToken?: (sessionToken: string) => void;
  private readonly random: () => number;
  private readonly forbidInsecureScheme: boolean;
  private readonly telemetry: ChatWSTelemetry;
  private readonly tracing: ChatWSTracing;

  private socket: WebSocket | null = null;
  private status: ChatWSStatus = 'idle';
  private isAuthenticated = false;
  private shouldReconnect = false;
  private reconnectAttempts = 0;
  private consecutiveFailedReconnects = 0;
  private reconnectFailureLogged = false;
  private reconnectTimer: ReturnType<typeof setTimeout> | null = null;
  private consecutiveAuthErrors = 0;
  private lastEventID: string | null = null;
  /** Timestamp (ms) when the current connection's socket 'open' event fired. Reset per-connection. */
  private socketOpenedAt: number | undefined = undefined;
  /**
   * True once we've completed at least one successful auth on this client.
   * Used to fire `rejoin` only on a *re-auth* (reconnect), never on the first
   * connect — otherwise we'd replay the previous turn into a fresh connection.
   */
  private hasAuthedOnce = false;

  /**
   * Monotonic ID bumped every time we open a new socket (or `disconnect()`).
   * Each event listener registered against a socket captures its
   * connection ID and no-ops when it no longer matches — that's how stale
   * sockets stop driving state after reconnect.
   */
  private connectionId = 0;

  /**
   * Shared in-flight `connect()` promise. While set, every caller of
   * `connect()` gets the same promise — no resolver mutation, no
   * fanned-out reject paths. Cleared once the attempt settles.
   */
  private connectPromise: Promise<void> | null = null;

  /**
   * Settle handles for the in-flight `openSocket()` promise. Held at the
   * class level so `disconnect()` can explicitly reject a pending
   * connect when bumping `connectionId` would otherwise leave it
   * permanently unsettled (the socket-event listeners that normally
   * call these are invalidated by the bump). Cleared on settle.
   */
  private connectSettle: { reject: (err: Error) => void } | null = null;

  private currentTurn: ChatWSTurnHandler | null = null;
  /** Token sent in the most recent auth frame, for the pre-send freshness check. */
  private authedToken: string | undefined = undefined;
  private pendingTurnFrame: PendingTurnFrame | null = null;
  /** The next auth_ok skips `rejoin`: the turn it would resync never started. */
  private suppressNextRejoin = false;
  private readonly statusListeners = new Set<ChatWSStatusListener>();

  constructor(config: ChatWSClientConfig) {
    this.url = config.url;
    this.chatID = config.chatID;
    this.getAccessToken = config.getAccessToken;
    this.getSessionToken = config.getSessionToken;
    this.getHostOrigin = config.getHostOrigin;
    this.authTimeoutMs = config.authTimeoutMs ?? DEFAULT_AUTH_TIMEOUT_MS;
    this.handshakeTurnRetryDeadlineMs =
      config.handshakeTurnRetryDeadlineMs ?? HANDSHAKE_TURN_RETRY_DEADLINE_MS;
    this.shouldReauthenticate = config.shouldReauthenticate;
    this.maxConsecutiveAuthErrors = config.maxConsecutiveAuthErrors ?? 3;
    this.logger = config.logger;
    this.WebSocketImpl = config.webSocketImpl ?? globalThis.WebSocket;
    this.onAuthFailed = config.onAuthFailed;
    this.onChatIDResolved = config.onChatIDResolved;
    this.onChatIDChanged = config.onChatIDChanged;
    this.onSessionToken = config.onSessionToken;
    this.random = config.random ?? Math.random;
    this.forbidInsecureScheme = config.forbidInsecureScheme ?? false;
    this.telemetry = config.telemetry ?? {
      socketClosed: () => {},
      socketOpened: () => {},
    };
    this.tracing = config.tracing ?? { auth: () => {}, socketOpen: () => {} };
  }

  // -----------------------------------------------------------------------
  // Public API
  // -----------------------------------------------------------------------

  /**
   * Open the socket and run the auth handshake. Resolves once the server
   * sends `auth_ok`; rejects on `auth_error`, timeout, or close-before-auth.
   *
   * Concurrent callers share one in-flight attempt (`connectPromise`); if
   * the socket is already open + authed, resolves immediately.
   */
  public async connect(): Promise<void> {
    this.shouldReconnect = true;
    if (this.isReady()) return Promise.resolve();
    if (this.connectPromise) return this.connectPromise;
    this.connectPromise = this.openSocket().finally(() => {
      this.connectPromise = null;
    });
    return this.connectPromise;
  }

  /**
   * Resume-path connect for tab/network return. Backgrounded tabs accumulate
   * failures on throttled timers (stale streak); cancel the pending backoff
   * and reset the streak for immediate recovery. Repeated resume events
   * resetting the streak is deliberate — each genuinely attempts recovery,
   * so only the sustained-outage diagnostic log can be deferred, never
   * recovery itself. `reconnectAttempts` is left intact so a resume reconnect
   * still reports its true attempt count to RUM via handleAuthOK. No-op when
   * torn down or already ready; in-flight connects are awaited, not duplicated.
   */
  public async resumeNow(): Promise<void> {
    if (!this.shouldReconnect || this.isReady()) return;
    this.clearReconnectTimer();
    this.resetReconnectFailureStreak();
    await this.connect();
  }

  /** Close the socket and stop reconnecting. Idempotent. */
  public disconnect(): void {
    this.shouldReconnect = false;
    this.clearPendingTurnFrame();
    this.suppressNextRejoin = false;
    this.clearReconnectTimer();
    this.resetReconnectFailureStreak();
    // Reject the in-flight connect attempt, if any. Bumping `connectionId`
    // below invalidates the socket event listeners (close handler in
    // particular) that would normally settle this promise — without this
    // explicit reject, the `connectPromise` would hang forever and any
    // subsequent `connect()` call on this instance would return the stale
    // pending promise.
    if (this.connectSettle) {
      const err = new Error('Chat WS disconnected');
      err.name = 'AbortError';
      this.connectSettle.reject(err);
    }
    // Invalidate every listener bound to the current socket — anything
    // they fire from now on (including the close event we're about to
    // trigger) will see `isCurrent() === false` and bail.
    this.connectionId += 1;
    const stale = this.socket;
    this.socket = null;
    this.isAuthenticated = false;
    this.setStatus('closed', false);
    if (stale) {
      try {
        stale.close();
      } catch (err: unknown) {
        this.logger?.warn({
          data: { error: String(err) },
          message: 'Error closing chat WS',
        });
      }
    }
    // Drop the registry pin so long-lived host pages (embed / widget
    // "New chat" remounts, tab-open/close loops) don't accumulate dead
    // clients. Guarded on `chatID` because JWT-bound clients set it in
    // the constructor and API-key clients set it via `bindChatID` after
    // `auth_ok` — either way, if we ever registered we clear.
    //
    // Safe alongside `chatWSConnectionManager.performTeardown` in
    // circuit-app, which guards its own `clearClient` call with
    // `if (getClient(chatID) === entry.client)` — our pre-clear just
    // makes that check a no-op, no double-free.
    if (this.chatID !== undefined) {
      registryClearClient(this.chatID);
    }
  }

  /**
   * Refresh the auth dependencies on a live client. The connection manager
   * calls this when a new acquirer arrives (e.g. the new-chat inline→route
   * handoff, or a quick navigate-away/back) so the long-lived client always
   * sends the freshest token on its next (re-)auth without forcing a
   * reconnect. No-op to the live socket: only the getters change, read on the
   * next handshake.
   */
  public updateAuth(deps: {
    getAccessToken: ChatWSTokenGetter;
    onAuthFailed?: () => void;
  }): void {
    this.getAccessToken = deps.getAccessToken;
    this.onAuthFailed = deps.onAuthFailed;
  }

  /**
   * Bind this client to `chatID` after the `auth_ok` handshake resolves it
   * (API-key transport only; JWT callers pass chatID at construction).
   */
  public bindChatID(chatID: string): void {
    if (this.chatID === chatID) return;
    if (this.chatID !== undefined) {
      this.logger?.warn({
        data: { existing: this.chatID, incoming: chatID },
        message: 'Chat WS bindChatID called on already-bound client; ignoring',
      });
      return;
    }
    this.chatID = chatID;
    registrySetClient(chatID, this);
    if (this.onChatIDResolved) {
      try {
        this.onChatIDResolved(chatID);
      } catch (err: unknown) {
        this.logger?.error({
          error: err,
          message: 'Chat WS onChatIDResolved callback threw',
        });
      }
    }
  }

  /**
   * Register the active turn handler. Subsequent inbound events route
   * to it.
   *
   * Throws `OverlappingTurnError` if a non-null handler is supplied
   * while one is already registered. Two overlapping turns on the
   * same client would silently overwrite each other otherwise, with
   * turn A's events getting routed to turn B's callbacks. The only
   * thing preventing that today is the disabled submit button; any
   * caller that bypasses it (agent action submission, microtask
   * races, future code paths) gets a loud failure instead of crossed
   * wires. Pass `null` to detach — always allowed.
   */
  public setTurnHandler(handler: ChatWSTurnHandler | null): void {
    if (handler !== null && this.currentTurn !== null) {
      const err = new Error(
        'Chat WS turn handler already active for this chat',
      );
      err.name = 'OverlappingTurnError';
      throw err;
    }
    const wasActive = this.currentTurn !== null;
    if (this.currentTurn !== handler) this.clearPendingTurnFrame();
    this.currentTurn = handler;
    // Notify idle listeners on the active→idle transition. The connection
    // manager uses this to defer teardown of an unmounted-but-streaming client
    // until its turn actually ends (keeps the client alive while the stream drains).
    if (wasActive && handler === null) {
      for (const listener of this.turnIdleListeners) {
        try {
          listener();
        } catch (err: unknown) {
          this.logger?.error({
            error: err,
            message: 'Chat WS turn idle listener threw',
          });
        }
      }
    }
  }

  private readonly turnIdleListeners = new Set<() => void>();

  /**
   * Subscribe to the active→idle transition (the turn handler being cleared,
   * i.e. a turn settling). Returns an unsubscribe fn. Used by the connection
   * manager to schedule teardown of an orphaned client only once its in-flight
   * turn has finished.
   */
  public onTurnIdle(listener: () => void): () => void {
    this.turnIdleListeners.add(listener);
    return () => {
      this.turnIdleListeners.delete(listener);
    };
  }

  /** True while a turn handler is registered. */
  public hasActiveTurn(): boolean {
    return this.currentTurn !== null;
  }

  /**
   * Send a `submit` frame for a new chat turn. Awaits an active connection
   * if needed; throws `NetworkError` if the frame can't be sent.
   */
  public async submit(opts: {
    messages: ChatWSPostInferenceMessageBlock[];
    requestID?: string;
    source: string;
    traceContext?: ChatTraceContext;
    sourceMetadata?: {
      action_display_text: string;
      action_name: string;
      display_text?: string;
    };
  }): Promise<void> {
    await this.ensureReadyForTurn();
    const payload = {
      request_id: opts.requestID,
      type: 'submit',
      ...traceContextFrameFields(opts.traceContext),
      messages: opts.messages,
      source: opts.source,
      source_metadata: opts.sourceMetadata,
    };
    this.sendFrameOrThrow(payload);
    this.trackPendingTurnFrame(payload, opts.requestID);
  }

  /**
   * Send a `resume_tool_use` frame. Awaits an active connection if needed;
   * throws `NetworkError` if the frame can't be sent.
   */
  public async resumeToolUse(opts: {
    requestID?: string;
    toolResults: ChatWSClientToolResult[];
    traceContext?: ChatTraceContext;
  }): Promise<void> {
    await this.ensureReadyForTurn();
    const payload = {
      request_id: opts.requestID,
      type: 'resume_tool_use',
      ...traceContextFrameFields(opts.traceContext),
      tool_results: opts.toolResults,
    };
    this.sendFrameOrThrow(payload);
    this.trackPendingTurnFrame(payload, opts.requestID);
  }

  /**
   * Send a `rejoin` frame to recover an in-flight turn on a *fresh* client
   * instance — the navigate-away-and-back case. Unlike the
   * private `sendRejoin()` (fired automatically on a same-instance re-auth),
   * this is the explicit mount-time recovery entry point: the previous mount's
   * socket was torn down, so this brand-new client has never seen any of the
   * turn's events and `lastEventID` is null. Awaits the connect + auth
   * handshake, then sends `rejoin` with `after = lastEventID` when a cursor is
   * known, otherwise omits `after` so the server replays the latest turn from
   * its start. Throws `NetworkError` if the frame can't be sent.
   */
  public async rejoin(): Promise<void> {
    await this.ensureReady();
    this.sendFrameOrThrow({
      after: this.lastEventID ?? undefined,
      type: 'rejoin',
    });
  }

  /**
   * Send a `cancel` frame. Best-effort: if the socket isn't ready, the
   * turn isn't running anyway, so we log + swallow rather than trying to
   * reconnect just to cancel.
   */
  public cancel(): void {
    // Before the isReady bail: auth_expired recovery has already nulled the socket.
    this.clearPendingTurnFrame();
    if (!this.isReady()) {
      this.logger?.warn({
        data: { authenticated: this.isAuthenticated, status: this.status },
        message: 'Chat WS cancel skipped — socket not ready',
      });
      return;
    }
    try {
      this.sendFrameOrThrow({ type: 'cancel' });
    } catch (err: unknown) {
      this.logger?.warn({
        data: { error: String(err) },
        message: 'Chat WS cancel send failed',
      });
    }
  }

  /** Subscribe to status transitions. Listener fires once with current state. */
  public onStatusChange(listener: ChatWSStatusListener): () => void {
    this.statusListeners.add(listener);
    try {
      listener(this.status, this.isAuthenticated);
    } catch (err: unknown) {
      this.logger?.error({
        error: err,
        message: 'Chat WS status listener error',
      });
    }
    return () => {
      this.statusListeners.delete(listener);
    };
  }

  public getStatus(): ChatWSStatus {
    return this.status;
  }

  public getIsAuthenticated(): boolean {
    return this.isAuthenticated;
  }

  /**
   * Debug helper for local reconnect/rejoin testing. Closes the underlying
   * socket without disabling reconnect, approximating a dropped connection.
   */
  public debugSimulateNetworkDrop(): boolean {
    const { socket } = this;
    if (
      !socket ||
      socket.readyState === this.WebSocketImpl.CLOSED ||
      socket.readyState === this.WebSocketImpl.CLOSING
    ) {
      return false;
    }
    try {
      socket.close();
      return true;
    } catch (err: unknown) {
      this.logger?.warn({
        data: { error: String(err) },
        message: 'Chat WS debug network drop failed',
      });
      return false;
    }
  }

  // -----------------------------------------------------------------------
  // Internals — readiness checks
  // -----------------------------------------------------------------------

  private isReady(): boolean {
    return (
      this.socket !== null &&
      this.socket.readyState === this.WebSocketImpl.OPEN &&
      this.isAuthenticated
    );
  }

  /**
   * Resolve once the socket is open + authed. If the auth has terminally
   * failed, throw immediately rather than retrying — `auth_failed` is the
   * client's terminal state until a new instance is constructed.
   */
  private async ensureReady(): Promise<void> {
    if (this.isReady()) return;
    if (this.status === 'auth_failed') {
      const err = new Error('Chat WS auth has failed; cannot send');
      err.name = 'AuthError';
      throw err;
    }
    await this.connect();
  }

  /**
   * `ensureReady` for a user-turn frame (submit / resume). A never-sent
   * submit can't be recovered by `rejoin`, so retry a retryable handshake
   * failure until the retry deadline elapses, then rethrow so the caller
   * degrades to NETWORK_ERROR. Non-retryable failures rethrow immediately.
   *
   * A sustained-outage streak counts every close since the last auth_ok — a
   * backgrounded tab racks those up while frozen without the outage
   * persisting — so instead of failing fast it triggers one immediate
   * `resumeNow()` bounded by SUSTAINED_OUTAGE_TURN_RETRY_DEADLINE_MS; a
   * genuine outage still degrades within ~5s.
   */
  private async ensureReadyForTurn(deadlineAt?: number): Promise<void> {
    this.reauthIfTokenExpired();
    const isSustainedOutage =
      this.shouldReconnect &&
      this.consecutiveFailedReconnects >= RECONNECT_FAILURE_ERROR_THRESHOLD;
    const deadline =
      deadlineAt ??
      Date.now() +
        (isSustainedOutage
          ? SUSTAINED_OUTAGE_TURN_RETRY_DEADLINE_MS
          : this.handshakeTurnRetryDeadlineMs);
    for (let attempt = 0; ; attempt += 1) {
      try {
        if (attempt === 0 && isSustainedOutage) {
          await this.resumeNow();
        } else {
          await this.ensureReady();
        }
        // resumeNow() no-ops on a torn-down client, so it can resolve without
        // connecting — fail here rather than at frame-send time.
        if (!this.isReady()) {
          const abortErr = new Error('Chat WS disconnected during resume');
          abortErr.name = 'AbortError';
          throw abortErr;
        }
        return;
      } catch (err) {
        if (!isRetryableHandshakeError(err) || Date.now() >= deadline)
          throw err;
        // `connect()` clears connectPromise on settle, so the next ensureReady()
        // opens a fresh socket. Signal unconditionally rather than deferring to
        // the socket's close handler: the retry's connect() bumps `connectionId`
        // first, so the (async, in real browsers) close no-ops via isCurrent().
        // Safe to double-signal — onReconnecting is idempotent consumer-side.
        this.dispatchTurnReconnecting();
        // Jittered politeness delay; the deadline check above is the real budget.
        await sleepMs(
          jitteredBackoffDelay({
            attempt,
            maxMs: HANDSHAKE_RETRY_BACKOFF_MAX_MS,
            minMs: HANDSHAKE_RETRY_BACKOFF_MIN_MS,
            random: this.random,
          }),
        );
        // `disconnect()` during the sleep sets shouldReconnect=false, but the
        // next `ensureReady()` -> `connect()` call flips it back to true —
        // without this guard a teardown mid-backoff would resurrect a zombie
        // connection instead of staying torn down.
        if (!this.shouldReconnect) {
          const abortErr = new Error(
            'Chat WS disconnected during retry backoff',
          );
          abortErr.name = 'AbortError';
          throw abortErr;
        }
      }
    }
  }

  private reauthIfTokenExpired(): void {
    if (!this.isReady() || !this.isAuthedTokenExpired()) return;
    this.dropSocketForReauth();
    this.suppressNextRejoin = true;
  }

  private isAuthedTokenExpired(): boolean {
    if (!this.shouldReauthenticate || this.authedToken === undefined) {
      return false;
    }
    try {
      return this.shouldReauthenticate(this.authedToken);
    } catch {
      return false;
    }
  }

  /**
   * Abandon the current socket so the next `ensureReady` opens a fresh one
   * (re-running auth with a new token). Unlike `disconnect()`, reconnecting
   * stays enabled and the client registry is untouched.
   */
  private dropSocketForReauth(): void {
    this.connectionId += 1;
    const stale = this.socket;
    this.socket = null;
    this.setStatus('closed', false);
    if (stale) {
      try {
        stale.close();
      } catch (err: unknown) {
        this.logger?.warn({
          data: { error: String(err) },
          message: 'Error closing chat WS for re-auth',
        });
      }
    }
  }

  private trackPendingTurnFrame(
    payload: Record<string, unknown>,
    requestID: string | undefined,
  ): void {
    if (requestID === undefined || !this.currentTurn) return;
    this.pendingTurnFrame = {
      handler: this.currentTurn,
      payload,
      requestID,
      resends: 0,
    };
  }

  // Deliberately leaves `suppressNextRejoin` set: a cancel()/handler swap can
  // land mid-handshake, and a replacement submit shares that handshake — a
  // rejoin before it would replay the old turn's `done` into the new handler.
  private clearPendingTurnFrame(): void {
    this.pendingTurnFrame = null;
  }

  /**
   * The BE rejects an expired-JWT turn frame with `auth_expired` (echoing
   * `request_id`) and leaves the socket open. True means the rejection was
   * consumed and the frame is being re-sent.
   */
  private tryRecoverAuthExpired(frame: WSOutboundFrame): boolean {
    const pending = this.pendingTurnFrame;
    if (!pending || frame.event !== 'error' || !this.shouldReconnect) {
      return false;
    }
    const envelope = frame.data as
      | { data?: { error_type?: string; request_id?: string } }
      | null
      | undefined;
    if (
      envelope?.data?.error_type !== AUTH_EXPIRED_ERROR_TYPE ||
      envelope.data.request_id !== pending.requestID ||
      pending.handler !== this.currentTurn ||
      pending.handler.ignoreAfterAbort.value
    ) {
      return false;
    }
    this.recoverAuthExpired().catch((err: unknown) => {
      this.logger?.error({
        error: err,
        message: 'Chat WS auth_expired recovery failed',
      });
    });
    return true;
  }

  private async recoverAuthExpired(): Promise<void> {
    const pending = this.pendingTurnFrame;
    if (!pending) return;
    const { handler } = pending;
    const isStale = (): boolean =>
      this.pendingTurnFrame !== pending ||
      this.currentTurn !== handler ||
      handler.ignoreAfterAbort.value ||
      !this.shouldReconnect;
    try {
      pending.deadline ??= Date.now() + this.handshakeTurnRetryDeadlineMs;
      if (
        pending.resends >= MAX_AUTH_EXPIRED_RESENDS ||
        Date.now() >= pending.deadline
      ) {
        throw new Error('Chat WS auth expired; turn not sent');
      }
      pending.resends += 1;
      this.dispatchTurnReconnecting();
      this.dropSocketForReauth();
      this.suppressNextRejoin = true;
      await this.ensureReadyForTurn(pending.deadline);
      if (isStale()) {
        if (this.pendingTurnFrame === pending) this.clearPendingTurnFrame();
        return;
      }
      this.sendFrameOrThrow(pending.payload);
    } catch (err: unknown) {
      // cancel()/disconnect()/a handler change already ended this turn's recovery.
      if (this.pendingTurnFrame !== pending) return;
      this.clearPendingTurnFrame();
      this.suppressNextRejoin = false;
      this.dispatchTurnError(markTurnNotStarted(err, { retagAuthError: true }));
    }
  }

  // -----------------------------------------------------------------------
  // Internals — socket lifecycle
  // -----------------------------------------------------------------------

  /**
   * Open a fresh socket and run the auth handshake. Returns a promise that
   * resolves on `auth_ok` and rejects on auth error, timeout, or
   * close-before-auth. Reconnect scheduling lives in the `close` handler.
   */
  private async openSocket(): Promise<void> {
    this.clearReconnectTimer();
    this.setStatus('connecting', false);
    this.connectionId += 1;
    const { connectionId } = this;
    const connectStartedAt = Date.now();

    return new Promise<void>((resolve, reject) => {
      let settled = false;
      const settleResolve = () => {
        if (settled) return;
        settled = true;
        this.connectSettle = null;
        resolve();
      };
      const settleReject = (err: Error) => {
        if (settled) return;
        settled = true;
        this.connectSettle = null;
        reject(err);
      };
      this.connectSettle = { reject: settleReject };

      const socket = this.openSocketOrThrow(settleReject);
      if (!socket) return;

      const isCurrent = (): boolean => this.connectionId === connectionId;
      const clearAuthTimer = scheduleAuthTimeout(
        isCurrent,
        socket,
        settleReject,
        this.authTimeoutMs,
      );

      this.socketOpenedAt = undefined;
      const ctx: SocketHandlerContext = {
        clearAuthTimer,
        connectStartedAt,
        isCurrent,
        settleReject,
        settleResolve,
        socket,
      };

      socket.addEventListener('open', () => this.onSocketOpen(ctx));
      socket.addEventListener('message', (event: MessageEvent) =>
        this.onSocketMessage(event, ctx),
      );
      socket.addEventListener('close', (event: CloseEvent) =>
        this.onSocketClose(event, ctx),
      );
      socket.addEventListener('error', (event) => {
        if (!isCurrent()) return;
        this.logger?.warn({
          data: { event: JSON.stringify(event) },
          message: 'Chat WS error event',
        });
      });
    });
  }

  /**
   * Validates the URL scheme and constructs the WebSocket. Returns the socket
   * on success; calls `settleReject` and returns null on any failure so the
   * caller can early-return without scheduling a reconnect (scheme errors) or
   * after scheduling one (constructor errors).
   */
  private openSocketOrThrow(
    settleReject: (err: Error) => void,
  ): WebSocket | null {
    // Fail loud on a scheme violation: a config error, not a transient
    // failure — reject without scheduling a reconnect.
    try {
      assertSecureWebSocketURL(this.url, {
        forbidPlaintext: this.forbidInsecureScheme,
      });
    } catch (err: unknown) {
      this.setStatus('closed', false);
      settleReject(err instanceof Error ? err : new Error(String(err)));
      return null;
    }

    try {
      const socket = new this.WebSocketImpl(this.url);
      this.socket = socket;
      return socket;
    } catch (err: unknown) {
      this.logger?.error({
        error: err,
        message: 'Failed to construct chat WebSocket',
      });
      this.scheduleReconnect();
      settleReject(err instanceof Error ? err : new Error(String(err)));
      return null;
    }
  }

  private onSocketOpen(ctx: SocketHandlerContext): void {
    if (!ctx.isCurrent()) return;
    const openedAt = Date.now();
    this.socketOpenedAt = openedAt;
    // Timing anchor for the socket_open Duration Vital.
    this.tracing.socketOpen({
      durationMs: openedAt - ctx.connectStartedAt,
      requestID: this.currentTurn?.requestID,
      startTimeMs: ctx.connectStartedAt,
    });
    // NOTE: don't reset `reconnectAttempts` here — the socket being open is
    // necessary but not sufficient. If auth fails, the backoff should keep
    // growing. Reset only on auth_ok (in onSocketMessage).
    this.sendAuth(ctx.socket).catch((err: unknown) => {
      if (!ctx.isCurrent()) return;
      ctx.clearAuthTimer();
      ctx.settleReject(err instanceof Error ? err : new Error(String(err)));
      try {
        ctx.socket.close();
      } catch {
        /* ignore */
      }
    });
  }

  private onSocketMessage(
    event: MessageEvent,
    ctx: SocketHandlerContext,
  ): void {
    if (!ctx.isCurrent()) return;
    const result = this.handleInbound(event.data);
    switch (result.kind) {
      case 'auth_ok':
        this.handleAuthOK(ctx, result);
        return;
      case 'auth_error':
        this.handleAuthError(result.message, ctx);
        break;
      case 'event':
      case 'noop':
      default:
        break;
    }
  }

  private handleAuthOK(
    ctx: SocketHandlerContext,
    result: Extract<InboundResult, { kind: 'auth_ok' }>,
  ): void {
    this.consecutiveAuthErrors = 0;
    this.resetReconnectFailureStreak();
    // Auth succeeded — *now* reset the reconnect backoff (not on 'open').
    // Read before the reset — 0 on a clean connect, >0 on a reconnect.
    const reconnectAttempt = this.reconnectAttempts;
    this.reconnectAttempts = 0;
    // Bind chatID and surface session_token BEFORE settleResolve so a caller
    // awaiting connect() sees a fully-resolved client.
    const previousChatID = this.chatID;
    if (result.chatID) this.bindChatID(result.chatID);
    if (result.sessionToken && this.onSessionToken) {
      try {
        this.onSessionToken(result.sessionToken);
      } catch (err: unknown) {
        this.logger?.error({
          error: err,
          message: 'Chat WS onSessionToken callback threw',
        });
      }
    }
    this.setStatus('open', true);
    this.telemetry.socketOpened({
      reconnectAttempt,
      requestID: this.currentTurn?.requestID,
    });
    // auth vital spans the 'open' → auth_ok leg.
    if (this.socketOpenedAt !== undefined) {
      this.tracing.auth({
        durationMs: Date.now() - this.socketOpenedAt,
        requestID: this.currentTurn?.requestID,
        startTimeMs: this.socketOpenedAt,
      });
    }
    const chatChanged =
      this.onChatIDChanged !== undefined &&
      this.hasAuthedOnce &&
      previousChatID !== undefined &&
      result.chatID !== undefined &&
      result.chatID !== previousChatID;
    // Resync only on a reconnect (re-auth), never the first connect.
    const suppressRejoin = this.suppressNextRejoin;
    this.suppressNextRejoin = false;
    const rejoinSent =
      this.hasAuthedOnce && !suppressRejoin && !chatChanged
        ? this.sendRejoin()
        : false;
    this.hasAuthedOnce = true;
    ctx.clearAuthTimer();
    ctx.settleResolve();
    // Notify the turn that reconnect completed — but only when rejoin went
    // out; a failed send means the socket is already dying again.
    if (rejoinSent) this.dispatchTurnReconnected();
    if (chatChanged && result.chatID !== undefined) {
      try {
        this.onChatIDChanged?.(previousChatID, result.chatID);
      } catch (err: unknown) {
        this.logger?.error({
          error: err,
          message: 'Chat WS onChatIDChanged callback threw',
        });
      }
      // A send parked on connect() resumes after this and fails its isReady() check.
      this.disconnect();
    }
  }

  private handleAuthError(message: string, ctx: SocketHandlerContext): void {
    this.consecutiveAuthErrors += 1;
    const err = new Error(message || 'Chat WS auth rejected');
    err.name = 'AuthError';
    ctx.clearAuthTimer();
    ctx.settleReject(err);
    if (this.consecutiveAuthErrors >= this.maxConsecutiveAuthErrors) {
      // Terminal: stop reconnecting and surface to the consumer.
      // Socket teardown happens via the close handler when we call socket.close().
      this.shouldReconnect = false;
      this.clearReconnectTimer();
      this.setStatus('auth_failed', false);
      this.onAuthFailed?.();
    }
    try {
      ctx.socket.close();
    } catch {
      /* ignore */
    }
  }

  private onSocketClose(event: CloseEvent, ctx: SocketHandlerContext): void {
    if (!ctx.isCurrent()) return;
    ctx.clearAuthTimer();
    this.consecutiveFailedReconnects += 1;
    const closeClassification = classifyClose({
      code: event.code,
      consecutiveFailedReconnects: this.consecutiveFailedReconnects,
      sustainedOutageAlreadyLogged: this.reconnectFailureLogged,
      wasAuthed: this.isAuthenticated,
    });
    if (closeClassification.reason === 'sustained-outage') {
      this.reconnectFailureLogged = true;
    }
    this.telemetry.socketClosed({
      closeCode: event.code,
      reason: event.reason || undefined,
      requestID: this.currentTurn?.requestID,
      wasClean: event.wasClean,
      // Additive field: existing dashboards read raw fields above.
      closeClassification: {
        level: closeClassification.level,
        reason: closeClassification.reason,
      },
    });
    // Single source of socket cleanup.
    this.socket = null;
    this.isAuthenticated = false;
    if (this.status !== 'auth_failed') {
      this.setStatus('closed', false);
    }
    // Exclude 1008 (policy violation) so a bad-token close isn't retried;
    // a real auth rejection already settled as AuthError before this fires.
    const closeErr = new Error('Chat WS closed before auth completed');
    if (event.code !== CLOSE_POLICY_VIOLATION) {
      closeErr.name = HANDSHAKE_CLOSED_ERROR_NAME;
    }
    ctx.settleReject(closeErr);
    if (this.shouldReconnect) {
      // Transparent mid-turn recovery: fire onReconnecting so the consumer
      // can surface a 'reconnecting' state. Guarded by hasAuthedOnce so a
      // never-authed first connect doesn't masquerade as a reconnect.
      if (this.currentTurn && this.hasAuthedOnce) {
        this.dispatchTurnReconnecting();
      }
      this.scheduleReconnect();
    } else if (this.currentTurn && this.hasAuthedOnce) {
      // Reconnecting is done (e.g. terminal auth failure) with a turn still
      // in flight — fail it so it can't hang forever. Guarded by hasAuthedOnce
      // so a brand-new connection that never authed surfaces via the
      // connect/submit promise instead (no double-fail).
      const err = new Error('Connection lost');
      err.name = 'NetworkError';
      this.dispatchTurnError(err);
    }
  }

  private scheduleReconnect(): void {
    this.clearReconnectTimer();
    const delay = jitteredBackoffDelay({
      attempt: this.reconnectAttempts,
      maxMs: RECONNECT_MAX_MS,
      minMs: RECONNECT_MIN_MS,
      random: this.random,
    });
    this.reconnectAttempts += 1;
    this.reconnectTimer = setTimeout(() => {
      if (!this.shouldReconnect) return;
      // Reconnect attempts share `connectPromise` with any concurrent
      // `connect()` callers, so wire it through the same gate.
      if (this.connectPromise) return;
      this.connectPromise = this.openSocket().finally(() => {
        this.connectPromise = null;
      });
      this.connectPromise.catch(() => {
        /* errors are surfaced via status + scheduleReconnect from close handler */
      });
    }, delay);
  }

  private clearReconnectTimer(): void {
    if (this.reconnectTimer) {
      clearTimeout(this.reconnectTimer);
      this.reconnectTimer = null;
    }
  }

  /** Clears the sustained-outage streak and its one-shot log latch. */
  private resetReconnectFailureStreak(): void {
    this.consecutiveFailedReconnects = 0;
    this.reconnectFailureLogged = false;
  }

  private setStatus(status: ChatWSStatus, isAuthenticated: boolean): void {
    this.status = status;
    this.isAuthenticated = isAuthenticated;
    for (const listener of this.statusListeners) {
      try {
        listener(status, isAuthenticated);
      } catch (err: unknown) {
        this.logger?.error({
          error: err,
          message: 'Chat WS status listener error',
        });
      }
    }
  }

  // -----------------------------------------------------------------------
  // Internals — auth handshake send
  // -----------------------------------------------------------------------

  private async sendAuth(socket: WebSocket): Promise<void> {
    if (socket.readyState !== this.WebSocketImpl.OPEN) {
      throw new Error('Chat WS socket closed before auth send');
    }
    let token: string;
    try {
      token = await Promise.resolve(this.getAccessToken());
    } catch (err: unknown) {
      this.logger?.error({
        error: err,
        message: 'Failed to retrieve chat WS access token',
      });
      throw err instanceof Error ? err : new Error(String(err));
    }
    if (token.length === 0) {
      throw new Error('No access token for chat WS auth');
    }
    this.authedToken = token;
    // API-key transport lets the adapter attach a stored
    // session_token to the auth frame so the server can resolve the
    // existing chat instead of minting a fresh one. Absent for JWT.
    const sessionToken = this.getSessionToken?.();
    const hostOrigin = this.getHostOrigin?.();
    const frame: Record<string, unknown> = { token, type: 'auth' };
    if (sessionToken) frame.session_token = sessionToken;
    if (hostOrigin) frame.host_origin = hostOrigin;
    this.rawSendOrThrow(socket, frame);
  }

  // -----------------------------------------------------------------------
  // Internals — inbound message dispatch
  // -----------------------------------------------------------------------

  private handleInbound(raw: unknown): InboundResult {
    const frame = parseRawFrame(raw, this.logger);
    if (!frame) return { kind: 'noop' };

    if (frame.event === 'auth_ok') {
      // API-key transport carries `chat_id` and `session_token`
      // on the auth_ok payload for post-auth chatID resolution + session
      // persistence. JWT transport omits both; the fields resolve to
      // undefined and the handshake still succeeds.
      const data = frame.data as
        | { chat_id?: string; session_token?: string; user_id?: string }
        | undefined;
      return {
        chatID: data?.chat_id,
        kind: 'auth_ok',
        sessionToken: data?.session_token,
        userID: data?.user_id,
      };
    }
    if (frame.event_id) this.lastEventID = frame.event_id;
    if (frame.event === 'auth_error') {
      const data = frame.data as { message?: string } | undefined;
      return { kind: 'auth_error', message: data?.message ?? '' };
    }
    // Dispatch the batched backlog in order, in this one task, so the store's
    // coalescing buffer applies it in a single flush (avoids the O(N^2) replay freeze).
    if (frame.event === 'batch') {
      this.handleBatchFrame(
        Array.isArray(frame.events) ? frame.events : [],
        raw,
      );
      return { kind: 'event' };
    }
    if (this.tryRecoverAuthExpired(frame)) return { kind: 'event' };
    this.dispatchTurnEvent(frame);
    return { kind: 'event' };
  }

  /**
   * Dispatch a `batch` frame's event backlog in order, in a single task, so the
   * store's coalescing buffer applies the replay in a single flush (avoids the
   * O(N²) replay freeze). Advances `lastEventID` per entry so a mid-replay
   * disconnect resumes from the correct cursor.
   */
  private handleBatchFrame(events: WSOutboundFrame[], raw: unknown): void {
    for (const inner of events) {
      if (inner == null || typeof inner.event !== 'string') {
        this.logger?.warn({
          data: { frame: raw },
          message: 'Chat WS batch entry missing event',
        });
      } else {
        if (inner.event_id) this.lastEventID = inner.event_id;
        this.dispatchTurnEvent(inner);
      }
    }
  }

  /**
   * Send the `rejoin` frame after a re-auth. Returns true when the frame was
   * actually sent (the caller uses this to gate the `onReconnected` turn
   * notification), false when there was nothing to recover or the send failed.
   */
  private sendRejoin(): boolean {
    // Resync only if there's something to recover: a cursor to resume after, or
    // an in-flight turn whose opening events we may have missed (the socket can
    // drop before any event_id-bearing frame arrives). With no cursor, omit
    // `after` so the server replays the latest turn from its start.
    if (!this.lastEventID && !this.currentTurn) return false;
    try {
      this.sendFrameOrThrow({
        after: this.lastEventID ?? undefined,
        type: 'rejoin',
      });
      return true;
    } catch (err: unknown) {
      this.logger?.warn({
        data: { error: String(err) },
        message: 'Chat WS rejoin send failed',
      });
      return false;
    }
  }

  private dispatchTurnEvent(frame: WSOutboundFrame): void {
    const handler = this.currentTurn;
    if (!handler || handler.ignoreAfterAbort.value) return;

    // frame.data IS the inference-event envelope: `{event, format_version, data}`.
    // The outer frame.event is just a duplicate routing tag. Pass the envelope through
    // unchanged so the renderer sees `event.data` as the actual payload (a
    // string for text, `{message: ...}` for error, etc.) and not the envelope
    // itself — that's the React #31 trap (object-as-child).
    const envelope = frame.data;
    if (envelope === undefined || envelope === null) {
      this.logger?.warn({
        data: { event: frame.event },
        message: 'Chat WS frame missing envelope',
      });
      return;
    }
    const streamEvent = envelope as unknown as StreamingEvent;
    // Carry the server's per-event id onto the dispatched event so the streaming
    // store can dedupe a replayed turn (a fresh-client `rejoin` with no `after`
    // cursor replays from the turn's start into a store that may already hold
    // those events on a soft navigate-back). Idempotency is keyed on this id;
    // events without one (synthetic client events, legacy frames) are never deduped.
    if (frame.event_id) {
      (streamEvent as { event_id?: string }).event_id = frame.event_id;
    }

    try {
      handler.onEvent(streamEvent);
    } catch (err: unknown) {
      this.logger?.error({ error: err, message: 'Chat WS turn handler threw' });
    }
  }

  private dispatchTurnError(err: Error): void {
    const handler = this.currentTurn;
    if (!handler || handler.ignoreAfterAbort.value) return;
    try {
      handler.onError(err);
    } catch (innerErr) {
      this.logger?.error({
        error: innerErr,
        message: 'Chat WS turn error handler threw',
      });
    }
  }

  /**
   * Notify the active turn handler that the socket dropped and we're
   * reconnecting. Best-effort and optional — mirrors `dispatchTurnError`'s
   * abort guard so events suppressed after a consumer abort stay suppressed.
   */
  private dispatchTurnReconnecting(): void {
    const handler = this.currentTurn;
    if (!handler || handler.ignoreAfterAbort.value || !handler.onReconnecting)
      return;
    try {
      handler.onReconnecting();
    } catch (innerErr) {
      this.logger?.error({
        error: innerErr,
        message: 'Chat WS turn reconnecting handler threw',
      });
    }
  }

  /**
   * Notify the active turn handler that a reconnect completed (re-auth +
   * rejoin sent). Mirrors `dispatchTurnReconnecting`'s abort guard.
   */
  private dispatchTurnReconnected(): void {
    const handler = this.currentTurn;
    if (!handler || handler.ignoreAfterAbort.value || !handler.onReconnected)
      return;
    try {
      handler.onReconnected();
    } catch (innerErr) {
      this.logger?.error({
        error: innerErr,
        message: 'Chat WS turn reconnected handler threw',
      });
    }
  }

  // -----------------------------------------------------------------------
  // Internals — outbound send
  // -----------------------------------------------------------------------

  /**
   * Send a frame on the current socket, or throw `NetworkError`. The
   * public `submit` / `resumeToolUse` callers go through `ensureReady()`
   * first, so this should always succeed; the throw exists to make the
   * "socket dropped between ensureReady and send" race explicit instead
   * of silently swallowing it.
   */
  private sendFrameOrThrow(payload: Record<string, unknown>): void {
    if (
      !this.socket ||
      this.socket.readyState !== this.WebSocketImpl.OPEN ||
      !this.isAuthenticated
    ) {
      const err = new Error('Chat WS not ready to send');
      err.name = 'NetworkError';
      this.logger?.warn({
        message: 'Chat WS frame dropped — socket not ready',
        data: {
          authenticated: this.isAuthenticated,
          status: this.status,
          type: payload.type,
        },
      });
      throw err;
    }
    this.rawSendOrThrow(this.socket, payload);
  }

  private rawSendOrThrow(
    socket: WebSocket,
    payload: Record<string, unknown>,
  ): void {
    let serialized: string;
    try {
      serialized = JSON.stringify(payload);
    } catch (err: unknown) {
      this.logger?.error({
        data: { type: payload.type },
        error: err,
        message: 'Chat WS frame serialize failed',
      });
      throw err instanceof Error ? err : new Error(String(err));
    }
    try {
      socket.send(serialized);
    } catch (err: unknown) {
      this.logger?.error({
        data: { type: payload.type },
        error: err,
        message: 'Chat WS send failed',
      });
      throw err instanceof Error ? err : new Error(String(err));
    }
  }

  /**
   * Returns the chat ID this client is bound to, or `undefined` when
   * construction omitted chatID and the auth_ok handshake hasn't landed yet
   * (API-key transport). See `bindChatID`.
   */
  public getChatID(): string | undefined {
    return this.chatID;
  }
}
